import { hasTaskOptimism } from "helpers/taskOptimism";
import {
  findTask,
  isTaskComplete,
  TASK_NAMES,
  useCaseTasks,
  type CaseTask,
} from "hooks/useCaseTasksAPI";

import type { HubTaskState } from "./startHubStatus";

function resolveTaskState({
  taskName,
  tasksAvailable,
  tasks,
}: {
  taskName: string;
  tasksAvailable: boolean;
  tasks: CaseTask[];
}): HubTaskState {
  if (!tasksAvailable) return "unavailable";

  const task = findTask(tasks, taskName);
  if (!task) return "unavailable";

  return isTaskComplete(task) ? "completed" : "open";
}

export type HubTaskStates = {
  vitalsTaskState: HubTaskState;
  arrangementTaskState: HubTaskState;
  consultationTaskState: HubTaskState;
  isLoadingTasks: boolean;
};

/**
 * Task System view of the three hub steps for a case, merged with the
 * optimistic completion latch. A cold fetch failure (error with no cached
 * response) degrades every state to "unavailable" so callers fall back to
 * their legacy per-domain signals — the tasks dependency can never blank
 * the hub.
 */
export function useHubTaskStates(
  caseId: string | null | undefined,
  { enabled }: { enabled: boolean },
): HubTaskStates {
  const { data, tasksAvailable, tasks, isLoading, error } = useCaseTasks(
    caseId,
    { enabled },
  );

  // The latch always resolves first: it was set after a successful domain
  // write, so it outranks everything — including a cold fetch failure,
  // which otherwise degrades to "unavailable" (legacy fallback).
  const coldError = !!error && !data;
  const resolve = (taskName: string): HubTaskState => {
    if (caseId && hasTaskOptimism(caseId, taskName)) return "completed";
    if (coldError) return "unavailable";
    return resolveTaskState({ taskName, tasksAvailable, tasks });
  };

  return {
    vitalsTaskState: resolve(TASK_NAMES.collectVitals),
    arrangementTaskState: resolve(TASK_NAMES.completeArrangements),
    consultationTaskState: resolve(TASK_NAMES.scheduleConsultation),
    isLoadingTasks: isLoading,
  };
}
